import React, { useEffect, useRef, useState, useCallback } from "react";
import { currencySymbol } from "../utils/currency";
import { FaTimes } from "react-icons/fa";
import { usersApi } from "../services/users";
import type { PayoutOrder, PayoutOrdersPage } from "../types/payouts";
import "./PayoutDetailsModal.css";
import { absMediaUrl } from "../utils/mediaUrl";
import { t, formatLocale } from "../i18n";
import { ReferralPayoutLine } from "./ReferralCashout";

interface Props {
  slug: string;
  batchId: string;
  payoutLabel?: string;
  /** Refer & Earn money this payout carried, per currency (Payout totals' referralBonus). */
  referralBonus?: Record<string, number> | null;
  onClose: () => void;
}

// Phase C: shared util — all 12 pay-in currencies.
function curSym(c?: string) {
  return currencySymbol((c || "usd").toLowerCase());
}

function fmtTime(iso: string) {
  const d = new Date(iso);
  return d.toLocaleTimeString(formatLocale(), { hour: "numeric", minute: "2-digit" });
}

// Cache last-opened payout's first page in module scope so reopening the
// same payout during a set is instant. Per spec: "cache the most recent
// payout details only".
let cachedBatchId: string | null = null;
let cachedFirstPage: PayoutOrdersPage | null = null;

const PayoutDetailsModal: React.FC<Props> = ({ slug, batchId, payoutLabel, referralBonus, onClose }) => {
  const [orders, setOrders] = useState<PayoutOrder[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [venueName, setVenueName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closing, setClosing] = useState(false);

  // Drag-to-dismiss state
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const dragStartY = useRef<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  const close = useCallback(() => {
    setClosing(true);
    setTimeout(onClose, 220);
  }, [onClose]);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // ESC to dismiss
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [close]);

  // Initial fetch — serve from cache if same batch was last viewed
  useEffect(() => {
    let cancelled = false;
    if (cachedBatchId === batchId && cachedFirstPage) {
      setOrders(cachedFirstPage.orders);
      setTotal(cachedFirstPage.total);
      setPage(cachedFirstPage.page);
      setHasMore(cachedFirstPage.hasMore);
      setVenueName(cachedFirstPage.venueName || null);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }
    setLoading(true);
    setError(null);
    usersApi
      .payoutOrdersPage(slug, batchId, { page: 1, limit: 50 })
      .then((res) => {
        if (cancelled) return;
        const data = res;
        setOrders(data.orders || []);
        setTotal(data.total || 0);
        setPage(data.page || 1);
        setHasMore(!!data.hasMore);
        setVenueName(data.venueName || null);
        cachedBatchId = batchId;
        cachedFirstPage = data;
      })
      .catch(() => {
        if (!cancelled) setError(t("payoutDetailsModal.couldnTLoadPayout"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [slug, batchId]);

  const loadMore = () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    usersApi
      .payoutOrdersPage(slug, batchId, { page: page + 1, limit: 50 })
      .then((res) => {
        const data = res;
        setOrders((prev) => [...prev, ...(data.orders || [])]);
        setPage(data.page || page + 1);
        setHasMore(!!data.hasMore);
      })
      .catch(() => {
        setError(t("payoutDetailsModal.couldnTLoadMore"));
      })
      .finally(() => setLoadingMore(false));
  };

  // Touch handlers — swipe-down to dismiss when at the top of the scroll
  const onTouchStart = (e: React.TouchEvent) => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    if (sheet.scrollTop > 0) return;
    dragStartY.current = e.touches[0].clientY;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) setDragOffset(delta);
  };

  const onTouchEnd = () => {
    if (dragStartY.current === null) return;
    if (dragOffset > 100) close();
    else setDragOffset(0);
    dragStartY.current = null;
  };

  return (
    <div
      className={`payout-details-overlay ${closing ? "is-closing" : ""}`}
      onClick={close}
      role="dialog"
      aria-modal="true"
      aria-label={t("payoutDetailsModal.payoutDetails2")}
    >
      <div
        ref={sheetRef}
        className={`payout-details-sheet ${closing ? "is-closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined }}
      >
        <div className="payout-details-handle" aria-hidden />

        <div className="payout-details-header">
          <div className="payout-details-header-left">
            <div className="payout-details-title">
              {payoutLabel ? t("payoutDetailsModal.payoutDetailsLabel", { label: payoutLabel }) : t("payoutDetailsModal.payoutDetails2")}
            </div>
            <div className="payout-details-subline">
              {venueName && <span className="payout-details-venue">{venueName}</span>}
              {!loading && total > 0 && (
                <span className="payout-details-count">
                  {t("payoutDetailsModal.ordersCount", { count: total })}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            className="payout-details-close"
            onClick={close}
            aria-label={t("payoutDetailsModal.close")}
          >
            <FaTimes size={16} />
          </button>
        </div>

        <div className="payout-details-body">
          {loading && (
            <div className="payout-details-empty">{t("payoutDetailsModal.loading")}</div>
          )}
          {!loading && error && (
            <div className="payout-details-empty payout-details-error">{error}</div>
          )}
          {/* Refer & Earn money this payout carried: its own line, above the requests (1.5.2). */}
          {!loading && !error && referralBonus && <ReferralPayoutLine byCurrency={referralBonus} />}
          {!loading && !error && orders.length === 0 && !referralBonus && (
            <div className="payout-details-empty">
              {t("payoutDetailsModal.allRequestsForThis")}
            </div>
          )}
          {!loading && !error && orders.length > 0 && (
            <ul className="payout-details-list">
              {orders.map((o) => {
                const requester = o.requesterInstagram
                  ? `@${o.requesterInstagram.replace(/^@+/, "")}`
                  : o.requesterName || null;
                const subline = [o.artists.join(", "), requester].filter(Boolean).join(" · ");
                return (
                  <li key={o._id} className={`payout-details-row payout-details-row--${o.status}`}>
                    <div className="payout-details-time">{fmtTime(o.createdAt)}</div>
                    {o.trackImage ? (
                      <img
                        src={absMediaUrl(o.trackImage) ?? undefined}
                        alt=""
                        className="payout-details-art"
                        loading="lazy"
                      />
                    ) : (
                      <div className="payout-details-art payout-details-art--ph" aria-hidden />
                    )}
                    <div className="payout-details-meta">
                      <div className="payout-details-song" title={o.title}>
                        {o.title || "—"}
                      </div>
                      {subline && <div className="payout-details-sub">{subline}</div>}
                    </div>
                    <div className="payout-details-right">
                      <span className="payout-details-amount">
                        {curSym(o.currency)}{o.amount.toFixed(2)}
                      </span>
                      <div className="payout-details-status-stack">
                        <span
                          className={`payout-details-status payout-details-status--${o.status}`}
                        >
                          {o.status === "declined" ? t("payoutDetailsModal.declined") : t("payoutDetailsModal.accepted")}
                        </span>
                        {o.disputed && (
                          <span
                            className="payout-details-status payout-details-status--disputed"
                            title={t("payoutDetailsModal.thisSongWasDisputed")}
                          >
                            {t("payoutDetailsModal.disputed")}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
              {hasMore && (
                <li className="payout-details-more">
                  <button
                    type="button"
                    className="payout-details-more-btn"
                    onClick={loadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? t("payoutDetailsModal.loading") : t("payoutDetailsModal.loadMore")}
                  </button>
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default PayoutDetailsModal;
